import { useMemo } from "react";
import { useSearchInfiniteContext } from "../../contexts/SearchInfinite/useSearchInfiniteContext";

/**
 * @param {object} params
 * @param {import("react").FC} params.SplashNoResult
 * @param {import("react").FC<{ error: import("../../types/Api/ApiError").ApiError}>} params.SplashError
 * @param {import("react").ReactElement} params.loadingElement
 */
export function buildDataHandlerInfiniteComponent({
  SplashNoResult,
  SplashError,
  loadingElement,
}) {
  /**
   * @template [T=any]
   * @param {object} props
   * @param {import("swr/infinite").SWRInfiniteResponse<T>} props.swrInfiniteResponse the SWR response
   * @param {boolean} [props.shouldRenderWithEmptyData] if true, the children will be rendered even if the data is empty
   * @param {import("react").ReactElement} [props.noResult] the element to render if the data is empty
   * @param {import("react").ReactElement} [props.loading] the element to render if the data is loading
   * @param {(children: import("react").ReactNode) => import("react").ReactElement} [props.wrapper] the wrapper that will be used to render any interal elements
   * @param {(response: T[]) => import("react").ReactElement} props.children
   */
  return function DataHandlerInfinite({
    swrInfiniteResponse,
    shouldRenderWithEmptyData = false,
    noResult,
    loading,
    wrapper = (children) => <>{children}</>,
    children,
  }) {
    const { form, autoSubmit } = useSearchInfiniteContext();

    const Loading = useMemo(() => {
      return loading ?? wrapper(loadingElement);
    }, [loading, wrapper]);

    if (
      (form.formState.isDirty && autoSubmit) ||
      (swrInfiniteResponse.data === undefined &&
        swrInfiniteResponse.error === undefined) ||
      (swrInfiniteResponse.isValidating &&
        swrInfiniteResponse.data === undefined)
    ) {
      return Loading;
    }

    if (swrInfiniteResponse.data !== undefined) {
      const apiResponses = swrInfiniteResponse.data;
      if (
        apiResponses.length === 1 &&
        // @ts-ignore
        (apiResponses[0]?.data?.length ?? 0) === 0 &&
        !shouldRenderWithEmptyData
      ) {
        return noResult ?? wrapper(<SplashNoResult />);
      }
      return children(swrInfiniteResponse.data);
    }

    if (!swrInfiniteResponse.isValidating && swrInfiniteResponse.error) {
      return wrapper(<SplashError error={swrInfiniteResponse.error} />);
    }

    return null;
  };
}
